import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';

import { PaymentInformation, PaymentMethod, PaymentMethodWithToken } from './payment.interface';
import { PlanAndBillingService } from '@services/plan-and-billing/plan-and-billing.service';
import { BusinessProfile } from '@shared/meta-data/business-profile.meta';
import { StorageService } from '@shared/services/storage/storage.service';
import { StripeCardToken } from '@shared/meta-data/stripe-card-token.meta';
import { environment } from '../../../../environments/environment';
import { BillingProviderEnum } from '@shared/meta-data';
import { STORE_KEYS } from '@app/utils/constants';

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss',
})
export class PaymentComponent implements OnInit {
  private readonly planService: PlanAndBillingService = inject(PlanAndBillingService);
  private readonly store: StorageService = inject(StorageService);
  private readonly toastrService: ToastrService = inject(ToastrService);

  @Input() defaultPaymentMethod: PaymentMethodWithToken;
  /** Subscription (or account) default card — cannot be deleted. */
  @Input() protectedPaymentMethodId?: string;
  @Input() isMakingPayment: boolean = false;
  @Input({
    transform: (details?: Partial<PaymentInformation>) => new PaymentInformation(details)
  }) paymentDetails: PaymentInformation = new PaymentInformation();
  @Output() selection: EventEmitter<PaymentMethodWithToken> = new EventEmitter();
  @Output() clearSelection: EventEmitter<null> = new EventEmitter();

  isShopifyBusiness: boolean;
  shopifyCreditBalance: number;

  paymentMethods: PaymentMethodWithToken[] = [];

  /* Card Payment Method properties */
  displayAddNewCardForm = false;
  tokenizingCard = false;
  cardDisplayStatus: 'isLoadingCards' | 'hasCards' | 'hasNoCard' = 'isLoadingCards';
  selectedCard: PaymentMethodWithToken;
  selectedShopifyBilling: 'credit' | 'external';
  deletingPaymentMethodId: string | null = null;

  get freezeAllActions(): boolean {
    return this.tokenizingCard || this.isMakingPayment || Boolean(this.deletingPaymentMethodId);
  }

  ngOnInit(): void {
    this.determineAccount();
  }

  determineAccount() {
    this.isShopifyBusiness = this.store.get<BusinessProfile>(STORE_KEYS.BUSINESS_PROFILE_KEY).shopify_billing;

    if (this.isShopifyBusiness) {
      this.fetchCreditBalance();
    } else {
      this.fetchUserPaymentMethod();
    }
  }

  emitSelection() {
    this.selection.emit({
      ...this.selectedCard,
      provider: this.isShopifyBusiness ? BillingProviderEnum.SHOPIFY : BillingProviderEnum.STRIPE,
      use_credits: this.selectedShopifyBilling === 'credit'
    });
  }

  fetchCreditBalance(): void {
    if (environment.FEATURE_FLAG.shopify_credit) {
      this.planService.fetchAtomicTaxCredits()
        .subscribe({
          next: ({ available_credits }) => this.shopifyCreditBalance = available_credits
        });
    }
  }

  refreshPaymentMethods(): void {
    this.fetchUserPaymentMethod();
  }

  fetchUserPaymentMethod() {
    this.cardDisplayStatus = 'isLoadingCards';
    this.planService.fetchPaymentMethod().subscribe(res => {
      this.paymentMethods = res ?? [];

      /**
       * If there is a default payment method provided,
       * ensure that it is the default selected whether it exist in the available methods or not
       */
      if (this.defaultPaymentMethod) {
        const paymentMethod = this.paymentMethods.find(pm => pm.id === this.defaultPaymentMethod.id);
        if (!paymentMethod && this.defaultPaymentMethod.id) {
          this.paymentMethods.push(this.defaultPaymentMethod);
        }
        this.selectCard(paymentMethod || this.defaultPaymentMethod);
      }

      this.setCardDisplayStatus();
    });
  }

  isDefaultPaymentMethod(paymentMethod: PaymentMethodWithToken): boolean {
    return Boolean(
      this.protectedPaymentMethodId
      && paymentMethod.id
      && paymentMethod.id === this.protectedPaymentMethodId,
    );
  }

  canDeleteCard(paymentMethod: PaymentMethodWithToken): boolean {
    if (!paymentMethod.id || this.freezeAllActions) {
      return false;
    }
    const savedCards = this.paymentMethods.filter((pm) => pm.id);
    if (savedCards.length <= 1) {
      return false;
    }
    return !this.isDefaultPaymentMethod(paymentMethod);
  }

  deleteCard(paymentMethod: PaymentMethodWithToken, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canDeleteCard(paymentMethod)) {
      return;
    }

    this.deletingPaymentMethodId = paymentMethod.id;
    this.planService.deletePaymentMethod(paymentMethod.id)
      .pipe(finalize(() => {
        this.deletingPaymentMethodId = null;
      }))
      .subscribe({
        next: () => {
          const wasSelected = this.selectedCard?.id === paymentMethod.id;
          this.paymentMethods = this.paymentMethods.filter((pm) => pm.id !== paymentMethod.id);
          if (wasSelected) {
            const fallback = this.paymentMethods.find((pm) => pm.id === this.protectedPaymentMethodId)
              || this.paymentMethods.find((pm) => pm.id);
            if (fallback) {
              this.selectCard(fallback);
            } else {
              this.selectedCard = null;
              this.clearSelection.emit();
            }
          }
          this.setCardDisplayStatus();
          this.toastrService.success('Card removed');
        },
        error: () => {
          this.toastrService.error('Could not remove this card. It may be your only or default card.');
        },
      });
  }

  handleCardTokenizeStartEvent() {
    this.tokenizingCard = true;
  }

  handleCardTokenizeErrorEvent() {
    this.tokenizingCard = false;
  }

  handleCardTokenizedEvent(card: StripeCardToken) {
    // Mind the order so that we can be able to unfreeze actions and select this card as default after adding it
    this.tokenizingCard = false;

    this.displayAddNewCardForm = false;

    // Construct a new payment method object from the card token
    const newPaymentMethod: PaymentMethodWithToken = {
      card: {
        brand: card.card.brand,
        last4: card.card.last4,
        exp_month: card.card.exp_month,
        exp_year: card.card.exp_year
      } as PaymentMethod['card'],
      token: card.id,
      id: undefined,
      metadata: '',
      type: 'card'
    };

    // Add the new constructed object to the list of payment methods displayed
    this.paymentMethods.push(newPaymentMethod);

    // Select the newly added payment method as default
    this.selectCard(newPaymentMethod);
    // toggle the card display
    this.setCardDisplayStatus();

  }

  selectCard(card: PaymentMethodWithToken) {
    if (this.freezeAllActions) {
      return;
    }

    // Do not select a card if there is a current process going on to add a new card or tokenize a card
    this.selectedCard = card;
    this.emitSelection();
  }

  toggleAddNewCardDisplay() {
    if (this.freezeAllActions) {
      return;
    }
    this.selectedCard = null;
    this.displayAddNewCardForm = true;
    this.clearSelection.emit();
  }

  private setCardDisplayStatus() {
    this.cardDisplayStatus = this.paymentMethods.length > 0 ? 'hasCards' : 'hasNoCard';
  }

  selectShopifyBilling(shopifyBilling: 'credit' | 'external'): void {
    this.selectedShopifyBilling = shopifyBilling;
  }
}
